import type { PropCategory } from "@circle-react-shared/BuilderUI/DataModel.types";
import type {
  StyleValue,
  StyleValueMeta,
} from "@circle-react-shared/BuilderUI/StyleValue.types";
import {
  DesignProp,
  GeneralProp,
} from "@circle-react-shared/BuilderUI/constants/PropCategories";

interface StyleOption {
  label: string;
  labelI18n: string;
  value: string;
}

export const getTextTransformOptions = (
  defaultValue: string = "none",
  category: PropCategory[] = [DesignProp, GeneralProp],
): StyleValue => {
  const options: StyleOption[] = [
    {
      label: "None",
      labelI18n: "builder_ui.prop_type.styles.text_transform_types.none",
      value: "none",
    },
    {
      label: "Uppercase",
      labelI18n: "builder_ui.prop_type.styles.text_transform_types.uppercase",
      value: "uppercase",
    },
    {
      label: "Lowercase",
      labelI18n: "builder_ui.prop_type.styles.text_transform_types.lowercase",
      value: "lowercase",
    },
    {
      label: "Capitalize",
      labelI18n: "builder_ui.prop_type.styles.text_transform_types.capitalize",
      value: "capitalize",
    },
  ];

  const meta: StyleValueMeta = {
    name: "Text Transform",
    nameI18n: "builder_ui.prop_type.styles.text_transform",
    category,
    options,
  };

  return {
    type: "select",
    value: defaultValue,
    meta,
  };
};

export const getFontStyleOptions = (
  defaultValue: string = "normal",
  category: PropCategory[] = [DesignProp, GeneralProp],
): StyleValue => {
  const options: StyleOption[] = [
    {
      label: "Normal",
      labelI18n: "builder_ui.prop_type.styles.font_style_types.normal",
      value: "normal",
    },
    {
      label: "Italic",
      labelI18n: "builder_ui.prop_type.styles.font_style_types.italic",
      value: "italic",
    },
    {
      label: "Oblique",
      labelI18n: "builder_ui.prop_type.styles.font_style_types.oblique",
      value: "oblique",
    },
  ];

  const meta: StyleValueMeta = {
    name: "Font Style",
    nameI18n: "builder_ui.prop_type.styles.font_style",
    category,
    options,
  };

  return {
    type: "select",
    value: defaultValue,
    meta,
  };
};

export const getFontWeightOptions = (
  defaultValue: string = "500",
  category: PropCategory[] = [DesignProp, GeneralProp],
): StyleValue => {
  const options: StyleOption[] = [
    {
      label: "Normal",
      labelI18n: "builder_ui.prop_type.styles.font_weight_types.normal",
      value: "400",
    },
    {
      label: "Medium",
      labelI18n: "builder_ui.prop_type.styles.font_weight_types.medium",
      value: "500",
    },
    {
      label: "Semibold",
      labelI18n: "builder_ui.prop_type.styles.font_weight_types.semibold",
      value: "600",
    },
    {
      label: "Bold",
      labelI18n: "builder_ui.prop_type.styles.font_weight_types.bold",
      value: "700",
    },
  ];

  const meta: StyleValueMeta = {
    name: "Font Weight",
    nameI18n: "builder_ui.prop_type.styles.font_weight",
    category,
    options,
  };

  return {
    type: "select",
    value: defaultValue,
    meta,
  };
};

export const getFontSizeOptions = (
  defaultValue: string = "16px",
  category: PropCategory[] = [DesignProp, GeneralProp],
): StyleValue => ({
  type: "slider",
  value: defaultValue,
  meta: {
    name: "Font Size",
    nameI18n: "builder_ui.prop_type.styles.font_size",
    category,
    sliderProps: {
      units: ["rem", "px"],
      step: 1,
      defaultUnit: "rem",
      min: 0,
      max: 500,
    },
  },
});

export const getLineHeightOptions = (
  defaultValue: string = "1.5",
  category: PropCategory[] = [DesignProp, GeneralProp],
): StyleValue => ({
  type: "number",
  value: defaultValue,
  meta: {
    name: "Line Height",
    nameI18n: "builder_ui.prop_type.styles.line_height",
    category,
    numberProps: {
      min: 0,
      max: 5,
      step: 0.1,
    },
  },
});

export const getTextAlignOptions = (
  defaultValue: string = "left",
  category: PropCategory[] = [DesignProp, GeneralProp],
): StyleValue => ({
  type: "alignment",
  value: defaultValue,
  meta: {
    name: "Text Align",
    nameI18n: "builder_ui.prop_type.styles.text_align",
    category,
    alignment_options: [
      {
        type: "left",
        icon: "20-builder-ui-style-text-align-left",
      },
      {
        type: "center",
        icon: "20-builder-ui-style-text-align-center",
      },
      {
        type: "right",
        icon: "20-builder-ui-style-text-align-right",
      },
      {
        type: "justify",
        icon: "20-builder-ui-style-text-align-justify",
      },
    ],
  },
});

export const getColorOptions = (
  defaultValue: string = "#000000",
  category: PropCategory[] = [DesignProp, GeneralProp],
): StyleValue => ({
  type: "color",
  value: defaultValue,
  meta: {
    name: "Color",
    nameI18n: "builder_ui.prop_type.styles.color",
    category,
  },
});

export const getTextDecorationOptions = (
  defaultValue: string = "none",
  category: PropCategory[] = [DesignProp, GeneralProp],
): StyleValue => ({
  type: "select",
  value: defaultValue,
  meta: {
    name: "Text Decoration",
    nameI18n: "builder_ui.prop_type.styles.text_decoration",
    category,
    options: [
      {
        label: "None",
        labelI18n: "builder_ui.prop_type.styles.text_decoration_types.none",
        value: "none",
      },
      {
        label: "Underline",
        labelI18n:
          "builder_ui.prop_type.styles.text_decoration_types.underline",
        value: "underline",
      },
      {
        label: "Overline",
        labelI18n: "builder_ui.prop_type.styles.text_decoration_types.overline",
        value: "overline",
      },
      {
        label: "Line Through",
        labelI18n:
          "builder_ui.prop_type.styles.text_decoration_types.line_through",
        value: "line-through",
      },
    ],
  },
});
